import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
// Note: ZoomableGroup scroll-zoom is disabled via filterZoomEvent prop

const API = 'https://coast-terminal-production.up.railway.app';

// ── Design tokens (locked) ────────────────────────────────────────────────────
const bg       = '#ffffff';
const surface  = '#fafaf8';
const gold     = '#b8956a';
const goldDim  = 'rgba(184,149,106,0.15)';
const border   = '#e6ddd4';
const primary  = '#0d3d5c';
const secondary= '#12526e';
const muted    = '#7a8a8a';
const white    = '#ffffff';
const green    = '#1A7A4A';
const red      = '#CC2200';

const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";
const fMono = "'JetBrains Mono', monospace";

// ── Market Data types ─────────────────────────────────────────────────────────
interface TickerQuote {
  symbol: string;
  name: string;
  price: number | null;
  change: number | null;
  change_pct: number | null;
  prev_close: number | null;
  week52_high: number | null;
  week52_low: number | null;
  week52_chg_pct: number | null;
  market_cap: number | null;
}
interface MarketDataResponse {
  sectors: Record<string, TickerQuote[]>;
  fetched_at: string;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface MetroRow {
  cbsa_code: string;
  name: string;
  metro_type: string;
  population: number | null;
  score_total: number | null;
  score_population: number | null;
  score_economic: number | null;
  score_housing: number | null;
  score_quality: number | null;
  rank: number;
  trend_direction: string;
  trend_strength: number | null;
  latitude: number | null;
  longitude: number | null;
}

interface BrainDeal {
  id: number;
  listing_id: string;
  source: string;
  title: string;
  address: string;
  city: string;
  state: string;
  asking_price: number | null;
  units: number | null;
  cap_rate: number | null;
  noi: number | null;
  property_class: string;
  year_built: number | null;
  description: string;
  listing_url: string;
  ai_score: number | null;
  ai_rationale: string;
  meets_criteria: boolean;
  flags: string;
  status: string;
  scraped_at: string;
  analyzed_at: string;
}

interface BrainScan {
  id: number;
  started_at: string;
  finished_at: string | null;
  deals_found: number;
  deals_analyzed: number;
  status: string;
  error_message: string | null;
}

interface BrainMemory {
  id: number;
  cbsa_code: string;
  market_name: string;
  analysis_text: string;
  opportunity_score: number | null;
  key_themes: string;
  last_updated: string;
}

interface Indicator {
  unit: string;
  source: string;
  series: { year: number; value: number }[];
}

interface MetroDetail {
  cbsa_code: string;
  name: string;
  metro_type: string;
  population: number | null;
  scores: {
    total: number | null;
    population: number | null;
    economic: number | null;
    housing: number | null;
    quality: number | null;
    rank_overall: number | null;
  };
  trend_direction: string;
  trend_strength: number | null;
  ai_thesis: string | null;
  indicators: Record<string, Indicator>;
}

interface Props {
  token: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined, decimals = 1): string => {
  if (n == null) return '—';
  return n.toFixed(decimals);
};

const fmtPop = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(0) + 'K';
  return n.toString();
};

const fmtVal = (value: number, unit: string): string => {
  if (unit === 'dollars') return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (unit === 'percent') return value.toFixed(1) + '%';
  if (unit === 'persons') return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (unit === 'index') return value.toFixed(1);
  if (unit === 'units') return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return value.toFixed(1);
};

const trendColor = (t: string) => t === 'rising' ? green : t === 'declining' ? red : gold;
const trendLabel = (t: string) => t === 'rising' ? '↑ Rising' : t === 'declining' ? '↓ Declining' : '→ Stable';

const scoreColor = (s: number | null) => {
  if (s == null) return muted;
  if (s >= 70) return green;
  if (s >= 50) return gold;
  return red;
};

// Score bar component
const ScoreBar = ({ score, label }: { score: number | null; label: string }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: '9px', color: muted, letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: fMono, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '14px', fontWeight: 700, color: scoreColor(score), fontFamily: fMono, marginBottom: 4 }}>
      {score != null ? score.toFixed(1) : '—'}
    </div>
    <div style={{ height: 3, backgroundColor: '#e0d8cc', borderRadius: 2 }}>
      <div style={{ height: 3, width: `${Math.min(score ?? 0, 100)}%`, backgroundColor: scoreColor(score), borderRadius: 2, transition: 'width 0.4s ease' }} />
    </div>
  </div>
);

// ── INDICATOR LABELS (human-readable) ────────────────────────────────────────
const IND_LABELS: Record<string, string> = {
  // Census ACS
  population:                  'Population',
  median_hhi:                  'Median Household Income',
  median_home_value:           'Median Home Value',
  median_gross_rent:           'Median Gross Rent',
  housing_vacancy_rate:        'Housing Vacancy Rate',
  bachelors_attainment_rate:   'Bachelor\'s Attainment',
  median_age:                  'Median Age',
  // IRS Migration
  migration_inflow:            'Migration Inflow',
  migration_outflow:           'Migration Outflow',
  migration_net:               'Net Migration',
  // BLS LAUS
  unemployment_rate_bls:       'Unemployment Rate (BLS Annual)',
  unemployment_rate_bls_recent:'Unemployment Rate (BLS Current)',
  employment_level:            'Employed Persons (BLS)',
  // FHFA
  house_price_index:           'House Price Index (FHFA)',
  // Census BPS
  building_permits_units:      'Building Permits (Units)',
  // Realtor.com
  listing_price:               'Median Listing Price',
  listing_price_current:       'Listing Price (Current Month)',
  listing_inventory:           'Active Listings',
  listing_inventory_current:   'Active Listings (Current)',
  // Redfin
  redfin_median_sale_price:    'Median Sale Price (Redfin)',
  redfin_median_dom:           'Days on Market (Redfin)',
  redfin_new_listings:         'New Listings (Redfin)',
  redfin_sale_price_current:   'Sale Price (Current Week)',
  redfin_dom_current:          'Days on Market (Current Week)',
  // DOL
  ui_claims_4wk_avg:           'UI Claims — 4-Week Avg',
};

// ── Market Data panel ─────────────────────────────────────────────────────────
function MarketData({ token }: { token: string }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState<MarketDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/searcher/market-data`, { headers })
      .then(r => { setData(r.data); setError(''); })
      .catch(() => setError('Market data unavailable — Yahoo Finance may be rate-limiting.'))
      .finally(() => setLoading(false));
  }, [token]);

  const fmtMktCap = (n: number | null) => {
    if (!n) return '—';
    if (n >= 1e12) return (n / 1e12).toFixed(2) + 'T';
    if (n >= 1e9)  return (n / 1e9).toFixed(1) + 'B';
    if (n >= 1e6)  return (n / 1e6).toFixed(0) + 'M';
    return n.toLocaleString();
  };

  const fmtPct = (n: number | null) => {
    if (n == null) return '—';
    return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
  };

  const fmtPrice = (n: number | null) => n == null ? '—' : '$' + n.toFixed(2);

  const changeColor = (n: number | null) => n == null ? muted : n >= 0 ? green : red;

  // 52-week position bar: where is current price between 52w low and high
  const WeekBar = ({ q }: { q: TickerQuote }) => {
    if (!q.price || !q.week52_low || !q.week52_high || q.week52_low >= q.week52_high) return null;
    const pct = Math.min(100, Math.max(0, ((q.price - q.week52_low) / (q.week52_high - q.week52_low)) * 100));
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: '9px', fontFamily: fMono, color: muted }}>{fmtPrice(q.week52_low)}</span>
        <div style={{ flex: 1, height: 3, backgroundColor: '#e0d8cc', borderRadius: 2, position: 'relative' }}>
          <div style={{ position: 'absolute', left: `${pct}%`, top: -2, width: 7, height: 7, borderRadius: '50%', backgroundColor: gold, transform: 'translateX(-50%)' }} />
        </div>
        <span style={{ fontSize: '9px', fontFamily: fMono, color: muted }}>{fmtPrice(q.week52_high)}</span>
      </div>
    );
  };

  if (loading) return (
    <div style={{ padding: '48px 0', color: muted, fontFamily: fBody, fontSize: 15, fontStyle: 'italic' }}>
      Fetching live market data…
    </div>
  );

  if (error) return (
    <div style={{ padding: '32px 0', color: red, fontFamily: fBody, fontSize: 14 }}>{error}</div>
  );

  if (!data) return null;

  return (
    <div>
      <div style={{ fontSize: '10px', fontFamily: fMono, color: muted, marginBottom: 20 }}>
        Live quotes via Yahoo Finance · As of {new Date(data.fetched_at).toLocaleTimeString()}
      </div>

      {Object.entries(data.sectors).map(([sector, tickers]) => (
        <div key={sector} style={{ marginBottom: 28 }}>
          {/* Sector label */}
          <div style={{ fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 10 }}>
            {sector}
          </div>

          {/* Ticker grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1, backgroundColor: border }}>
            {tickers.map(q => (
              <div key={q.symbol} style={{ backgroundColor: white, padding: '14px 16px' }}>
                {/* Symbol + name */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: '13px', fontFamily: fMono, fontWeight: 700, color: primary }}>{q.symbol}</div>
                  <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{fmtMktCap(q.market_cap)}</div>
                </div>
                <div style={{ fontSize: '10px', fontFamily: fBody, color: muted, marginBottom: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.name}</div>

                {/* Price + day change */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                  <div style={{ fontSize: '20px', fontFamily: fMono, fontWeight: 700, color: primary }}>{fmtPrice(q.price)}</div>
                  <div style={{ fontSize: '12px', fontFamily: fMono, fontWeight: 600, color: changeColor(q.change_pct) }}>
                    {fmtPct(q.change_pct)}
                  </div>
                </div>
                <div style={{ fontSize: '10px', fontFamily: fMono, color: muted, marginBottom: 10 }}>
                  {q.change != null ? (q.change >= 0 ? '+' : '') + q.change.toFixed(2) : '—'} today
                </div>

                {/* 52-week bar */}
                <WeekBar q={q} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: '9px', fontFamily: fMono, color: muted }}>52W</span>
                  <span style={{ fontSize: '9px', fontFamily: fMono, color: changeColor(q.week52_chg_pct), fontWeight: 600 }}>
                    {fmtPct(q.week52_chg_pct)} YTD
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── US MAP ────────────────────────────────────────────────────────────────────
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const TARGET_CBSAS = new Set([
  "42660","29820","19740","19100","28140",
  "12060","16740","45300","12420","31080","34980","35380",
]);

function USMap({ token, onOpenDetail }: { token: string; onOpenDetail: (cbsa: string) => void }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [metros, setMetros] = useState<MetroRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState<MetroRow | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [scoreFilter, setScoreFilter] = useState<'all' | 'strong' | 'moderate' | 'weak'>('all');
  const [popFilter, setPopFilter] = useState('');
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<[number, number]>([-97, 38]);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [stateData, setStateData]         = useState<any>(null);
  const [stateLoading, setStateLoading]   = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/searcher/rankings`, { headers, params: { limit: 1000, offset: 0 } })
      .then(r => setMetros(r.data.metros || []))
      .finally(() => setLoading(false));
  }, [token]);

  const openState = (stateAbbr: string, stateName: string) => {
    setSelectedState(stateName);
    setStateData(null);
    setStateLoading(true);
    axios.get(`${API}/brain/state/${stateAbbr}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStateData(r.data))
      .catch(() => setStateData({ error: true }))
      .finally(() => setStateLoading(false));
  };

  const visible = metros.filter(m => {
    if (!m.latitude || !m.longitude) return false;
    // Exclude Hawaii/Alaska dots that are way out of range for continental view
    if (m.longitude < -160 || m.longitude > -60) return false;
    if (m.latitude < 24 || m.latitude > 50) return false;
    const pop = m.population ?? 0;
    if (popFilter === '1000000' && pop < 1_000_000) return false;
    if (popFilter === '500000'  && pop < 500_000)   return false;
    if (popFilter === '250000'  && pop < 250_000)   return false;
    if (popFilter === '100000'  && pop < 100_000)   return false;
    const s = m.score_total ?? 0;
    if (scoreFilter === 'strong'   && s < 65) return false;
    if (scoreFilter === 'moderate' && (s < 45 || s >= 65)) return false;
    if (scoreFilter === 'weak'     && s >= 45) return false;
    return true;
  });

  const dotR = (m: MetroRow): number => {
    const pop = m.population ?? 0;
    if (TARGET_CBSAS.has(m.cbsa_code)) return 7;
    if (pop >= 3_000_000) return 6;
    if (pop >= 1_000_000) return 5;
    if (pop >= 500_000)   return 4;
    if (pop >= 250_000)   return 3;
    return 2;
  };

  const dotColor = (m: MetroRow): string => {
    if (TARGET_CBSAS.has(m.cbsa_code)) return gold;
    const s = m.score_total ?? 50;
    if (s >= 65) return green;
    if (s >= 48) return secondary;
    return red;
  };

  const selSt: React.CSSProperties = {
    padding: '7px 12px', backgroundColor: '#f2ede6',
    border: `1px solid ${border}`, color: primary,
    fontFamily: fMono, fontSize: '11px', cursor: 'pointer', outline: 'none',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 6 }}>
          National Market Map
        </div>
        <div style={{ fontSize: '28px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.1, marginBottom: 4 }}>
          U.S. Metro Intelligence
        </div>
        <div style={{ fontSize: '14px', fontFamily: fBody, color: muted, fontStyle: 'italic' }}>
          {visible.length} markets plotted · <span style={{ color: gold, fontStyle: 'normal' }}>●</span> Target markets · Click any state for CRE data · Click any city for full market profile
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Score Filter</div>
          <select value={scoreFilter} onChange={e => setScoreFilter(e.target.value as typeof scoreFilter)} style={selSt}>
            <option value="all">All Markets</option>
            <option value="strong">Strong (65+)</option>
            <option value="moderate">Moderate (45–65)</option>
            <option value="weak">Weak (&lt;45)</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Min Population</div>
          <select value={popFilter} onChange={e => setPopFilter(e.target.value)} style={selSt}>
            <option value="">All Sizes</option>
            <option value="1000000">Major (1M+)</option>
            <option value="500000">Large (500K+)</option>
            <option value="250000">Mid-size (250K+)</option>
            <option value="100000">Small (100K+)</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
          <button onClick={() => setZoom(z => Math.min(z * 1.5, 8))} style={{ ...selSt, padding: '7px 14px', fontWeight: 700 }}>+</button>
          <button onClick={() => setZoom(z => Math.max(z / 1.5, 1))} style={{ ...selSt, padding: '7px 14px', fontWeight: 700 }}>−</button>
          <button onClick={() => { setZoom(1); setCenter([-97, 38]); }} style={{ ...selSt, fontSize: '10px' }}>Reset</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { c: gold,      l: 'Target Market' },
          { c: green,     l: 'Strong Score (65+)' },
          { c: secondary, l: 'Moderate Score (45–65)' },
          { c: red,       l: 'Weak Score (<45)' },
        ].map(({ c, l }) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: c }} />
            <span style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Map */}
      <div
        ref={mapRef}
        style={{ backgroundColor: '#d6e8f0', border: `1px solid ${border}`, position: 'relative', overflow: 'hidden', userSelect: 'none' }}
      >
        {loading && (
          <div style={{ position: 'absolute', top: 16, left: 16, fontSize: '12px', fontFamily: fMono, color: muted, zIndex: 5 }}>
            Loading market data…
          </div>
        )}

        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 900 }}
          width={900}
          height={520}
          style={{ width: '100%', height: 'auto' }}
        >
          <ZoomableGroup
            zoom={zoom}
            center={center}
            onMoveEnd={(pos: any) => {
              setCenter(pos.coordinates as [number, number]);
              // Only update zoom from programmatic buttons, not scroll events
            }}
            filterZoomEvent={(evt: any) => {
              // Block scroll-wheel zoom, allow everything else (drag pan works)
              return evt.type !== 'wheel' && evt.type !== 'dblclick';
            }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }: { geographies: any[] }) =>
                geographies.map(geo => {
                  const props = geo.properties || {};
                  const abbr  = props.postal || props.STUSPS || props.STATE_ABBR || '';
                  const name  = props.name  || props.NAME  || abbr;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => { if (abbr) openState(abbr, name); }}
                      style={{
                        default:  { fill: '#f0ede6', stroke: '#c8bfa8', strokeWidth: 0.5, outline: 'none', cursor: 'pointer' },
                        hover:    { fill: '#f2ede6', stroke: '#b8956a', strokeWidth: 1.2, outline: 'none', cursor: 'pointer' },
                        pressed:  { fill: '#d4c4a0', stroke: '#b8956a', strokeWidth: 1.2, outline: 'none', cursor: 'pointer' },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Non-target metros */}
            {visible
              .filter(m => !TARGET_CBSAS.has(m.cbsa_code))
              .map(m => (
                <Marker
                  key={m.cbsa_code}
                  coordinates={[m.longitude!, m.latitude!]}
                  onClick={() => onOpenDetail(m.cbsa_code)}
                  onMouseEnter={(e: any) => {
                    setHovered(m);
                    const rect = mapRef.current?.getBoundingClientRect();
                    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }}
                  onMouseLeave={() => setHovered(null)}
                >
                  <circle
                    r={dotR(m) / zoom * 1.2}
                    fill={dotColor(m)}
                    fillOpacity={0.75}
                    stroke="white"
                    strokeWidth={0.4 / zoom}
                    style={{ cursor: 'pointer' }}
                  />
                </Marker>
              ))}

            {/* Target markets — rendered on top */}
            {visible
              .filter(m => TARGET_CBSAS.has(m.cbsa_code))
              .map(m => (
                <Marker
                  key={m.cbsa_code}
                  coordinates={[m.longitude!, m.latitude!]}
                  onClick={() => onOpenDetail(m.cbsa_code)}
                  onMouseEnter={(e: any) => {
                    setHovered(m);
                    const rect = mapRef.current?.getBoundingClientRect();
                    if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                  }}
                  onMouseLeave={() => setHovered(null)}
                >
                  <circle
                    r={(dotR(m) + 2) / zoom * 1.2}
                    fill={gold}
                    fillOpacity={0.9}
                    stroke="white"
                    strokeWidth={1.2 / zoom}
                    style={{ cursor: 'pointer' }}
                  />
                  {zoom >= 2 && (
                    <text
                      textAnchor="middle"
                      y={-8 / zoom}
                      style={{ fontSize: `${9 / zoom}px`, fontFamily: fMono, fill: primary, fontWeight: 700, pointerEvents: 'none' }}
                    >
                      {m.name.split(',')[0].split('-')[0]}
                    </text>
                  )}
                </Marker>
              ))}
          </ZoomableGroup>
        </ComposableMap>

        {/* Hover tooltip */}
        {hovered && (
          <div style={{
            position: 'absolute',
            left: Math.min(tooltipPos.x + 12, (mapRef.current?.offsetWidth ?? 400) - 240),
            top: Math.max(tooltipPos.y - 10, 10),
            backgroundColor: white,
            border: `1px solid ${border}`,
            padding: '12px 16px',
            zIndex: 20,
            minWidth: 220,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
          }}>
            <div style={{ fontSize: '13px', fontFamily: fHead, fontWeight: 700, color: primary, marginBottom: 2, lineHeight: 1.2 }}>
              {hovered.name}
            </div>
            {TARGET_CBSAS.has(hovered.cbsa_code) && (
              <div style={{ fontSize: '9px', fontFamily: fMono, color: gold, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6, fontWeight: 700 }}>
                ★ Target Market
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 6 }}>
              {[
                ['Overall',    hovered.score_total],
                ['Economic',   hovered.score_economic],
                ['Housing',    hovered.score_housing],
                ['Population', hovered.score_population],
              ].map(([l, v]) => (
                <div key={l as string}>
                  <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1px' }}>{l}</div>
                  <div style={{ fontSize: '14px', fontFamily: fMono, fontWeight: 700, color: scoreColor(v as number | null) }}>{fmt(v as number | null)}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>
              Rank #{hovered.rank} · {fmtPop(hovered.population)}
            </div>
            <div style={{ fontSize: '10px', fontFamily: fMono, fontWeight: 700, color: trendColor(hovered.trend_direction), marginTop: 3 }}>
              {trendLabel(hovered.trend_direction)}
            </div>
            <div style={{ fontSize: '9px', fontFamily: fMono, color: gold, marginTop: 6, letterSpacing: '0.5px' }}>
              Click to open full data profile →
            </div>
          </div>
        )}
      </div>
      <div style={{ fontSize: '10px', fontFamily: fMono, color: muted, marginTop: 8 }}>
        Click any state for CRE legislation & tax data · Drag to pan when zoomed · {visible.length} markets shown
      </div>

      {/* ── State CRE Panel ── */}
      {selectedState && (
        <div style={{
          marginTop: 28,
          border: `1px solid ${border}`,
          backgroundColor: white,
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        }}>
          {/* Panel header */}
          <div style={{
            backgroundColor: primary,
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', fontFamily: fMono, letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: 4 }}>
                State CRE Intelligence Terminal
              </div>
              <div style={{ fontSize: '22px', fontFamily: fHead, fontWeight: 700, color: white, lineHeight: 1.1 }}>
                {stateData?.name || selectedState}
              </div>
            </div>
            <button
              onClick={() => { setSelectedState(null); setStateData(null); }}
              style={{ background: 'none', border: `1px solid rgba(255,255,255,0.25)`, color: 'rgba(255,255,255,0.65)', fontFamily: fMono, fontSize: '11px', padding: '6px 14px', cursor: 'pointer', letterSpacing: '1px' }}
            >
              CLOSE ×
            </button>
          </div>

          {stateLoading && (
            <div style={{ padding: '32px 24px', color: muted, fontFamily: fBody, fontStyle: 'italic', fontSize: 14 }}>
              Loading state data…
            </div>
          )}

          {stateData?.error && (
            <div style={{ padding: '24px', color: red, fontFamily: fBody, fontSize: 14 }}>
              State data unavailable. Please try again.
            </div>
          )}

          {stateData && !stateData.error && (
            <div style={{ padding: '24px' }}>

              {/* Key metrics strip */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 1,
                backgroundColor: border,
                marginBottom: 24,
              }}>
                {[
                  { label: 'Property Tax', value: stateData.property_tax_rate },
                  { label: 'Transfer Tax', value: stateData.transfer_tax },
                  { label: 'Income Tax', value: stateData.income_tax },
                  { label: 'Cap Gains', value: stateData.cap_gains_rate },
                  { label: 'Landlord Rating', value: stateData.landlord_friendly },
                  { label: 'Rent Control', value: stateData.rent_control },
                  { label: '1031 Exchange', value: stateData['1031_exchange'] ? 'Conforms' : 'Does not conform' },
                  { label: 'Foreign Restrictions', value: stateData.foreign_investment_restrictions ? 'Yes — check details' : 'None' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ backgroundColor: surface, padding: '14px 16px' }}>
                    <div style={{ fontSize: '8.5px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 5 }}>{label}</div>
                    <div style={{
                      fontSize: '12px', fontFamily: fMono, fontWeight: 700,
                      color: label === 'Landlord Rating'
                        ? (['A', 'A-', 'A+'].includes(String(value)) ? green : ['B+', 'B', 'B-'].includes(String(value)) ? gold : red)
                        : label === 'Foreign Restrictions' && value !== 'None' ? red
                        : primary,
                      lineHeight: 1.3,
                    }}>
                      {String(value)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Two-column content */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

                {/* Incentives */}
                <div>
                  <div style={{ fontSize: '9px', color: gold, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>
                    Key Investor Incentives
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(stateData.key_incentives || []).map((item: string, i: number) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ color: green, fontFamily: fMono, fontSize: '12px', lineHeight: 1.5, flexShrink: 0 }}>+</div>
                        <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.5 }}>{item}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Risks */}
                <div>
                  <div style={{ fontSize: '9px', color: red, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>
                    Key Risks & Headwinds
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(stateData.key_risks || []).map((item: string, i: number) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ color: red, fontFamily: fMono, fontSize: '12px', lineHeight: 1.5, flexShrink: 0 }}>−</div>
                        <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.5 }}>{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Legislation */}
              {stateData.recent_legislation?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: '9px', color: gold, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>
                    Recent Legislation & Policy
                  </div>
                  <div style={{ border: `1px solid ${border}`, backgroundColor: surface, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(stateData.recent_legislation || []).map((item: string, i: number) => (
                      <div key={i} style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.5, paddingBottom: i < stateData.recent_legislation.length - 1 ? 6 : 0, borderBottom: i < stateData.recent_legislation.length - 1 ? `1px solid ${border}` : 'none' }}>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    Depreciation Recapture
                  </div>
                  <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.5 }}>{stateData.depreciation_recapture}</div>
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                    Deed Recording Fee
                  </div>
                  <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.5 }}>{stateData.deed_recording_fee}</div>
                </div>
              </div>

              {/* Market notes */}
              <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16 }}>
                <div style={{ fontSize: '9px', color: gold, fontFamily: fMono, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>
                  CRE Market Assessment
                </div>
                <div style={{ fontSize: '15px', fontFamily: fBody, color: primary, lineHeight: 1.7, fontStyle: 'italic' }}>
                  {stateData.cre_market_notes}
                </div>
              </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── BRAIN DEALS PANEL ─────────────────────────────────────────────────────────
function BrainPanel({ token }: { token: string }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [deals, setDeals]         = useState<BrainDeal[]>([]);
  const [memory, setMemory]       = useState<BrainMemory[]>([]);
  const [scan, setScan]           = useState<BrainScan | null>(null);
  const [loading, setLoading]     = useState(false);
  const [scanning, setScanning]   = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView]           = useState<'deals' | 'memory' | 'criteria'>('deals');

  // Criteria state
  const [criteria, setCriteria]   = useState<any>(null);
  const [criteriaLoading, setCriteriaLoading] = useState(false);
  const [criteriaSaving, setCriteriaSaving]   = useState(false);
  const [criteriaEdit, setCriteriaEdit] = useState<any>(null);
  const [minScore, setMinScore]   = useState('60');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter]     = useState('');
  const [expanded, setExpanded]   = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadDeals = useCallback(() => {
    setLoading(true);
    const params: any = { limit: 100, offset: 0 };
    if (minScore)     params.min_score = parseInt(minScore);
    if (statusFilter) params.status    = statusFilter;
    if (cityFilter)   params.city      = cityFilter;
    axios.get(`${API}/brain/deals`, { headers, params })
      .then(r => setDeals(r.data.deals || []))
      .finally(() => setLoading(false));
  }, [minScore, statusFilter, cityFilter, token]);

  const loadMemory = useCallback(() => {
    axios.get(`${API}/brain/memory`, { headers })
      .then(r => setMemory(r.data.memory || r.data.markets || []));
  }, [token]);

  const loadScanStatus = useCallback(() => {
    axios.get(`${API}/brain/scan/status`, { headers })
      .then(r => setScan(r.data));
  }, [token]);

  const loadCriteria = useCallback(() => {
    setCriteriaLoading(true);
    axios.get(`${API}/brain/criteria`, { headers })
      .then(r => { setCriteria(r.data); setCriteriaEdit(r.data); })
      .catch(() => {})
      .finally(() => setCriteriaLoading(false));
  }, [token]);

  const saveCriteria = async () => {
    if (!criteriaEdit) return;
    setCriteriaSaving(true);
    try {
      const res = await axios.put(`${API}/brain/criteria`, criteriaEdit, { headers });
      setCriteria(res.data);
      setCriteriaEdit(res.data);
    } catch {}
    setCriteriaSaving(false);
  };

  useEffect(() => {
    loadDeals();
    loadMemory();
    loadScanStatus();
    loadCriteria();
  }, []);

  useEffect(() => { loadDeals(); }, [minScore, statusFilter, cityFilter]);

  // Poll scan status while running
  useEffect(() => {
    if (scan?.status === 'running') {
      pollRef.current = setTimeout(() => {
        loadScanStatus();
        loadDeals();
      }, 8000);
    }
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [scan]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/brain/scan`, {}, { headers });
      setTimeout(() => { loadScanStatus(); }, 2000);
    } catch {}
    setScanning(false);
  };

  const triggerMemoryRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/brain/memory/refresh`, {}, { headers });
      setTimeout(() => { loadMemory(); }, 5000);
    } catch {}
    setRefreshing(false);
  };

  const updateStatus = async (id: number, status: string) => {
    await axios.post(`${API}/brain/deals/${id}/status`, { status }, { headers });
    loadDeals();
  };

  const fmtPrice = (n: number | null) => {
    if (!n) return '—';
    if (n >= 1e9)  return '$' + (n / 1e9).toFixed(1) + 'B';
    if (n >= 1e6)  return '$' + (n / 1e6).toFixed(1) + 'M';
    return '$' + n.toLocaleString();
  };

  const statusColors: Record<string, string> = {
    new:      primary,
    reviewed: secondary,
    flagged:  gold,
    dismissed: muted,
  };

  const scoreGrade = (s: number | null): string => {
    if (!s) return '—';
    if (s >= 85) return 'STRONG BUY';
    if (s >= 70) return 'GOOD FIT';
    if (s >= 55) return 'POSSIBLE';
    return 'WEAK FIT';
  };

  const scoreGradeColor = (s: number | null): string => {
    if (!s) return muted;
    if (s >= 85) return green;
    if (s >= 70) return secondary;
    if (s >= 55) return gold;
    return red;
  };

  const selSt: React.CSSProperties = {
    padding: '7px 12px', backgroundColor: '#f2ede6',
    border: `1px solid ${border}`, color: primary,
    fontFamily: fMono, fontSize: '11px', cursor: 'pointer', outline: 'none',
  };

  const isScanning = scan?.status === 'running';
  const parsedFlags = (flags: string): string[] => {
    try { return JSON.parse(flags) || []; } catch { return flags ? [flags] : []; }
  };
  const parsedThemes = (t: string): string[] => {
    try { return JSON.parse(t) || []; } catch { return t ? [t] : []; }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 6 }}>
          Autonomous Acquisition Intelligence
        </div>
        <div style={{ fontSize: '28px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.1, marginBottom: 4 }}>
          The Brain
        </div>
        <div style={{ fontSize: '14px', fontFamily: fBody, color: muted, fontStyle: 'italic', lineHeight: 1.6 }}>
          Continuously scans Crexi and LoopNet for multifamily listings matching the fund's acquisition criteria —
          Class A/B+, 50+ units, $20M+, across 12 target markets.
          Claude analyzes each deal and scores it 0–100 against the fund's investment thesis.
        </div>
      </div>

      {/* Scan status bar */}
      <div style={{ backgroundColor: white, border: `1px solid ${border}`, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {scan ? (
            <>
              <div>
                <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Last Scan</div>
                <div style={{ fontSize: '12px', fontFamily: fMono, color: primary, fontWeight: 700 }}>
                  {scan.started_at ? new Date(scan.started_at).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Status</div>
                <div style={{ fontSize: '12px', fontFamily: fMono, fontWeight: 700, color: isScanning ? gold : (scan.status === 'completed' ? green : red) }}>
                  {isScanning ? '⟳ SCANNING…' : scan.status?.toUpperCase()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Deals Found</div>
                <div style={{ fontSize: '12px', fontFamily: fMono, color: primary, fontWeight: 700 }}>{scan.deals_found ?? '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Analyzed</div>
                <div style={{ fontSize: '12px', fontFamily: fMono, color: primary, fontWeight: 700 }}>{scan.deals_analyzed ?? '—'}</div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: '12px', fontFamily: fMono, color: muted, fontStyle: 'italic' }}>No scans run yet. Start the Brain to begin sourcing deals.</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={triggerScan}
            disabled={scanning || isScanning}
            style={{ padding: '9px 20px', backgroundColor: (scanning || isScanning) ? surface : primary, color: white, border: 'none', fontFamily: fMono, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: (scanning || isScanning) ? 'default' : 'pointer', opacity: (scanning || isScanning) ? 0.7 : 1 }}
          >
            {isScanning ? 'Scanning…' : scanning ? 'Starting…' : '⟳ Run Scan'}
          </button>
          <button
            onClick={triggerMemoryRefresh}
            disabled={refreshing}
            style={{ padding: '9px 16px', backgroundColor: 'transparent', border: `1px solid ${border}`, color: primary, fontFamily: fMono, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh Memory'}
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${border}`, marginBottom: 0 }}>
        {([['deals', `Active Deals (${deals.length})`], ['memory', `Market Memory (${memory.length})`], ['criteria', 'Acquisition Criteria']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            style={{
              padding: '8px 20px', background: 'none', border: 'none',
              borderBottom: view === id ? `2px solid ${gold}` : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer',
              color: view === id ? gold : muted,
              fontFamily: fMono, fontSize: '11px', letterSpacing: '1.5px',
              textTransform: 'uppercase', fontWeight: view === id ? 700 : 400,
            }}
          >{label}</button>
        ))}
      </div>

      {/* ── DEALS VIEW ── */}
      {view === 'deals' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, padding: '14px 0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Min AI Score</div>
              <select value={minScore} onChange={e => setMinScore(e.target.value)} style={selSt}>
                <option value="">Any</option>
                <option value="85">85+ Strong Buy</option>
                <option value="70">70+ Good Fit</option>
                <option value="55">55+ Possible</option>
                <option value="0">All Scored</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selSt}>
                <option value="">All</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="flagged">Flagged</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>City</div>
              <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} style={selSt}>
                <option value="">All Markets</option>
                {['Seattle','Las Vegas','Denver','Dallas','Kansas City','Atlanta','Charlotte','Tampa','Austin','Los Angeles','Nashville','New Orleans'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '40px 0', color: muted, fontFamily: fBody, fontSize: 15, fontStyle: 'italic' }}>Loading deals…</div>
          ) : deals.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <div style={{ fontSize: '15px', fontFamily: fBody, color: muted, fontStyle: 'italic', marginBottom: 12 }}>
                No deals found yet. Run a scan to source listings from Crexi and LoopNet.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: border }}>
              {deals.map((deal, i) => {
                const isExpanded = expanded === deal.id;
                const flags = parsedFlags(deal.flags);
                return (
                  <div key={deal.id} style={{ backgroundColor: i % 2 === 0 ? white : bg }}>
                    {/* Summary row */}
                    <div
                      onClick={() => setExpanded(isExpanded ? null : deal.id)}
                      style={{ padding: '16px 20px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '80px 1fr 120px 90px 80px 80px 100px', gap: 12, alignItems: 'center' }}
                    >
                      {/* AI Score */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '26px', fontFamily: fMono, fontWeight: 900, color: scoreGradeColor(deal.ai_score), lineHeight: 1 }}>{deal.ai_score ?? '—'}</div>
                        <div style={{ fontSize: '8px', fontFamily: fMono, color: scoreGradeColor(deal.ai_score), letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700 }}>{scoreGrade(deal.ai_score)}</div>
                      </div>

                      {/* Title + address */}
                      <div>
                        <div style={{ fontSize: '13px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.2, marginBottom: 3 }}>{deal.title || '—'}</div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{deal.address || `${deal.city}, ${deal.state}`}</div>
                        {flags.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                            {flags.slice(0, 2).map((f, fi) => (
                              <span key={fi} style={{ fontSize: '9px', fontFamily: fMono, color: gold, backgroundColor: goldDim, padding: '2px 6px', borderRadius: 2 }}>{f}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Price */}
                      <div>
                        <div style={{ fontSize: '16px', fontFamily: fMono, fontWeight: 700, color: primary }}>{fmtPrice(deal.asking_price)}</div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>Asking Price</div>
                      </div>

                      {/* Units */}
                      <div>
                        <div style={{ fontSize: '14px', fontFamily: fMono, fontWeight: 700, color: primary }}>{deal.units ?? '—'}</div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>Units</div>
                      </div>

                      {/* Cap rate */}
                      <div>
                        <div style={{ fontSize: '14px', fontFamily: fMono, fontWeight: 700, color: deal.cap_rate ? (deal.cap_rate >= 5 ? green : deal.cap_rate >= 4 ? gold : red) : muted }}>
                          {deal.cap_rate ? deal.cap_rate.toFixed(1) + '%' : '—'}
                        </div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>Cap Rate</div>
                      </div>

                      {/* Class */}
                      <div>
                        <div style={{ fontSize: '14px', fontFamily: fMono, fontWeight: 700, color: primary }}>{deal.property_class || '—'}</div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>Class</div>
                      </div>

                      {/* Status */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <div style={{ fontSize: '10px', fontFamily: fMono, fontWeight: 700, color: statusColors[deal.status] || muted, textTransform: 'uppercase', letterSpacing: '1px' }}>{deal.status}</div>
                        <div style={{ fontSize: '9px', fontFamily: fMono, color: muted }}>{isExpanded ? '▲ collapse' : '▼ expand'}</div>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${border}`, padding: '20px 20px 20px', backgroundColor: surface }}>
                        {/* AI Rationale */}
                        {deal.ai_rationale && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: '9px', fontFamily: fMono, color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Claude's Analysis</div>
                            <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{deal.ai_rationale}</div>
                          </div>
                        )}

                        {/* Flags */}
                        {flags.length > 0 && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: '9px', fontFamily: fMono, color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Risk Flags</div>
                            {flags.map((f, fi) => (
                              <div key={fi} style={{ fontSize: '12px', fontFamily: fBody, color: primary, marginBottom: 3 }}>• {f}</div>
                            ))}
                          </div>
                        )}

                        {/* Listing description */}
                        {deal.description && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Listing Description</div>
                            <div style={{ fontSize: '12px', fontFamily: fBody, color: muted, lineHeight: 1.6, maxHeight: 120, overflow: 'hidden' }}>{deal.description.slice(0, 600)}{deal.description.length > 600 ? '…' : ''}</div>
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {deal.listing_url && (
                            <a href={deal.listing_url} target="_blank" rel="noreferrer"
                              style={{ padding: '8px 16px', backgroundColor: primary, color: white, fontFamily: fMono, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none' }}>
                              View Listing ↗
                            </a>
                          )}
                          {(['reviewed', 'flagged', 'dismissed'] as const).map(s => (
                            <button key={s} onClick={() => updateStatus(deal.id, s)}
                              style={{ padding: '8px 14px', backgroundColor: 'transparent', border: `1px solid ${border}`, color: statusColors[s] || muted, fontFamily: fMono, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>
                              Mark {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── MEMORY VIEW ── */}
      {view === 'memory' && (
        <div>
          {memory.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <div style={{ fontSize: '15px', fontFamily: fBody, color: muted, fontStyle: 'italic', marginBottom: 12 }}>
                No market memory yet. Click "Refresh Memory" to generate Claude's institutional analysis of each target market.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: border, marginTop: 1 }}>
              {memory.map((m, i) => {
                const themes = parsedThemes(m.key_themes);
                return (
                  <div key={m.id} style={{ backgroundColor: i % 2 === 0 ? white : bg, padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 16 }}>
                      <div>
                        <div style={{ fontSize: '16px', fontFamily: fHead, fontWeight: 700, color: primary, marginBottom: 3 }}>{m.market_name}</div>
                        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>
                          Last analyzed: {m.last_updated ? new Date(m.last_updated).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      {m.opportunity_score != null && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: '28px', fontFamily: fMono, fontWeight: 900, color: scoreColor(m.opportunity_score), lineHeight: 1 }}>
                            {m.opportunity_score}
                          </div>
                          <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1px' }}>Opportunity Score</div>
                        </div>
                      )}
                    </div>
                    {m.analysis_text && (
                      <div style={{ fontSize: '13px', fontFamily: fBody, color: primary, lineHeight: 1.75, marginBottom: 12 }}>
                        {m.analysis_text}
                      </div>
                    )}
                    {themes.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {themes.map((t, ti) => (
                          <span key={ti} style={{ fontSize: '10px', fontFamily: fMono, color: primary, backgroundColor: goldDim, padding: '3px 10px', border: `1px solid ${border}` }}>{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── CRITERIA VIEW ── */}
      {view === 'criteria' && (
        <div style={{ paddingTop: 24 }}>
          {criteriaLoading ? (
            <div style={{ color: muted, fontFamily: fBody, fontSize: 14, fontStyle: 'italic' }}>Loading criteria…</div>
          ) : !criteriaEdit ? (
            <div style={{ color: muted, fontFamily: fBody, fontSize: 14, fontStyle: 'italic' }}>Could not load criteria.</div>
          ) : (
            <div>
              <div style={{ fontSize: '13px', fontFamily: fBody, color: muted, fontStyle: 'italic', marginBottom: 24, lineHeight: 1.6 }}>
                These criteria govern what The Brain scans for and how Claude scores each listing.
                Changes take effect on the next scan.
              </div>

              {/* Section: Financials */}
              <div style={{ backgroundColor: white, border: `1px solid ${border}`, marginBottom: 2 }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
                  Financial Thresholds
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1, backgroundColor: border }}>
                  {[
                    { label: 'Min Units', key: 'min_units', type: 'number', suffix: 'units', placeholder: '50' },
                    { label: 'Min Price', key: 'min_price', type: 'number', suffix: '$', placeholder: '20000000' },
                    { label: 'Max Price', key: 'max_price', type: 'number', suffix: '$', placeholder: 'No limit' },
                    { label: 'Min Cap Rate', key: 'min_cap_rate', type: 'number', suffix: '%', placeholder: 'Any' },
                    { label: 'Max LTV', key: 'max_ltv', type: 'number', suffix: '%', placeholder: '60' },
                    { label: 'Min DSCR', key: 'min_dscr', type: 'number', suffix: 'x', placeholder: '1.30' },
                  ].map(({ label, key, suffix, placeholder }) => (
                    <div key={key} style={{ backgroundColor: bg, padding: '16px 20px' }}>
                      <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {suffix === '$' && <span style={{ fontFamily: fMono, fontSize: 13, color: muted }}>$</span>}
                        <input
                          type="number"
                          value={criteriaEdit[key] ?? ''}
                          onChange={e => setCriteriaEdit((p: any) => ({ ...p, [key]: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                          placeholder={placeholder}
                          style={{ flex: 1, padding: '6px 10px', backgroundColor: white, border: `1px solid ${border}`, fontFamily: fMono, fontSize: 13, color: primary, outline: 'none', width: '100%' }}
                        />
                        {suffix !== '$' && <span style={{ fontFamily: fMono, fontSize: 12, color: muted }}>{suffix}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section: Target Markets */}
              <div style={{ backgroundColor: white, border: `1px solid ${border}`, marginBottom: 2 }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
                  Target Markets
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: '11px', fontFamily: fMono, color: muted, marginBottom: 12 }}>Toggle markets on/off. The Brain will only source listings from active markets.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[
                      'Seattle','Las Vegas','Denver','Dallas','Kansas City',
                      'Atlanta','Charlotte','Tampa','Austin','Los Angeles',
                      'Nashville','New Orleans',
                      // Extended markets user can add
                      'Miami','Phoenix','San Diego','San Francisco','New York',
                      'Chicago','Boston','Portland','San Antonio','Raleigh',
                    ].map(city => {
                      const active = (criteriaEdit.target_markets || []).includes(city);
                      return (
                        <button
                          key={city}
                          onClick={() => setCriteriaEdit((p: any) => ({
                            ...p,
                            target_markets: active
                              ? (p.target_markets || []).filter((c: string) => c !== city)
                              : [...(p.target_markets || []), city],
                          }))}
                          style={{
                            padding: '6px 14px',
                            backgroundColor: active ? primary : 'transparent',
                            color: active ? white : muted,
                            border: `1px solid ${active ? primary : border}`,
                            fontFamily: fMono, fontSize: '11px', cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          {active ? '✓ ' : ''}{city}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Section: Property Classes */}
              <div style={{ backgroundColor: white, border: `1px solid ${border}`, marginBottom: 2 }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
                  Property Classes
                </div>
                <div style={{ padding: '16px 20px', display: 'flex', gap: 8 }}>
                  {['A', 'B+', 'B', 'B-', 'C+'].map(cls => {
                    const active = (criteriaEdit.property_classes || []).includes(cls);
                    return (
                      <button
                        key={cls}
                        onClick={() => setCriteriaEdit((p: any) => ({
                          ...p,
                          property_classes: active
                            ? (p.property_classes || []).filter((c: string) => c !== cls)
                            : [...(p.property_classes || []), cls],
                        }))}
                        style={{
                          padding: '8px 20px',
                          backgroundColor: active ? gold : 'transparent',
                          color: active ? white : muted,
                          border: `1px solid ${active ? gold : border}`,
                          fontFamily: fMono, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                        }}
                      >
                        Class {cls}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section: Deal Types */}
              <div style={{ backgroundColor: white, border: `1px solid ${border}`, marginBottom: 2 }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
                  Deal Types
                </div>
                <div style={{ padding: '16px 20px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['Stabilized', 'Build to Core', 'Value-Add', 'Lease-Up', 'Ground-Up', 'Distressed'].map(type => {
                    const active = (criteriaEdit.deal_types || []).includes(type);
                    return (
                      <button
                        key={type}
                        onClick={() => setCriteriaEdit((p: any) => ({
                          ...p,
                          deal_types: active
                            ? (p.deal_types || []).filter((t: string) => t !== type)
                            : [...(p.deal_types || []), type],
                        }))}
                        style={{
                          padding: '7px 16px',
                          backgroundColor: active ? secondary : 'transparent',
                          color: active ? white : muted,
                          border: `1px solid ${active ? secondary : border}`,
                          fontFamily: fMono, fontSize: '11px', cursor: 'pointer',
                        }}
                      >
                        {active ? '✓ ' : ''}{type}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section: Notes */}
              <div style={{ backgroundColor: white, border: `1px solid ${border}`, marginBottom: 20 }}>
                <div style={{ padding: '12px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
                  Additional Instructions for Claude
                </div>
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Amenity Requirements</div>
                    <textarea
                      value={criteriaEdit.amenity_notes || ''}
                      onChange={e => setCriteriaEdit((p: any) => ({ ...p, amenity_notes: e.target.value }))}
                      rows={3}
                      style={{ width: '100%', padding: '10px 14px', backgroundColor: bg, border: `1px solid ${border}`, fontFamily: fBody, fontSize: 13, color: primary, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>Additional Investment Notes</div>
                    <textarea
                      value={criteriaEdit.additional_notes || ''}
                      onChange={e => setCriteriaEdit((p: any) => ({ ...p, additional_notes: e.target.value }))}
                      rows={4}
                      style={{ width: '100%', padding: '10px 14px', backgroundColor: bg, border: `1px solid ${border}`, fontFamily: fBody, fontSize: 13, color: primary, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>

              {/* Save button */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button
                  onClick={saveCriteria}
                  disabled={criteriaSaving}
                  style={{ padding: '11px 32px', backgroundColor: criteriaSaving ? surface : primary, color: white, border: 'none', fontFamily: fMono, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', cursor: criteriaSaving ? 'default' : 'pointer', opacity: criteriaSaving ? 0.7 : 1 }}
                >
                  {criteriaSaving ? 'Saving…' : 'Save Criteria'}
                </button>
                <button
                  onClick={() => setCriteriaEdit(criteria)}
                  style={{ padding: '11px 20px', backgroundColor: 'transparent', border: `1px solid ${border}`, color: muted, fontFamily: fMono, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}
                >
                  Reset
                </button>
                {!criteriaSaving && criteria && (
                  <span style={{ fontSize: '11px', fontFamily: fMono, color: muted }}>
                    Last saved: {criteria.updated_at ? new Date(criteria.updated_at).toLocaleString() : 'Never'}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── CRE type union ────────────────────────────────────────────────────────────
type CreType = 'all' | 'multifamily' | 'industrial' | 'office' | 'retail';

interface MetroWithCRE extends MetroRow { creScore: number; }

// ── THE BRAIN ─────────────────────────────────────────────────────────────────
function TheBrain({ token, onOpenDetail }: { token: string; onOpenDetail: (cbsa: string) => void }) {
  const headers = { Authorization: `Bearer ${token}` };

  const [allMetros, setAllMetros]         = useState<MetroRow[]>([]);
  const [loading, setLoading]             = useState(false);
  const [hoveredCbsa, setHoveredCbsa]     = useState<string | null>(null);
  const [xAxis, setXAxis]                 = useState<'economic' | 'housing' | 'population' | 'quality'>('economic');
  const [yAxis, setYAxis]                 = useState<'economic' | 'housing' | 'population' | 'quality'>('housing');
  const [colorBy, setColorBy]             = useState<'total' | 'trend' | 'size'>('total');
  const [sizeFilter, setSizeFilter]       = useState('');
  const [creType, setCreType]             = useState<CreType>('all');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/searcher/rankings`, { headers, params: { limit: 1000, offset: 0 } })
      .then(r => setAllMetros(r.data.metros || []))
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = allMetros.filter(m => {
    const pop = m.population ?? 0;
    if (sizeFilter === '1000000') return pop >= 1_000_000;
    if (sizeFilter === '500000')  return pop >= 500_000;
    if (sizeFilter === '250000')  return pop >= 250_000;
    if (sizeFilter === '100000')  return pop >= 100_000;
    return true;
  });

  const creScore = (m: MetroRow, type: CreType): number => {
    const ec = m.score_economic  ?? 50;
    const hh = m.score_housing   ?? 50;
    const pp = m.score_population ?? 50;
    const qq = m.score_quality   ?? 50;
    switch (type) {
      case 'multifamily': return pp * 0.35 + ec * 0.30 + hh * 0.25 + qq * 0.10;
      case 'industrial':  return ec * 0.40 + pp * 0.35 + hh * 0.15 + qq * 0.10;
      case 'office':      return ec * 0.40 + qq * 0.30 + pp * 0.20 + hh * 0.10;
      case 'retail':      return pp * 0.35 + ec * 0.30 + qq * 0.25 + hh * 0.10;
      default:            return (m.score_total ?? 50);
    }
  };

  const creWeights: Record<CreType, { label: string; weight: string }[]> = {
    multifamily: [
      { label: 'Population Momentum', weight: '35%' },
      { label: 'Economic Velocity',   weight: '30%' },
      { label: 'Housing Signal',      weight: '25%' },
      { label: 'Quality of Life',     weight: '10%' },
    ],
    industrial: [
      { label: 'Economic Velocity',   weight: '40%' },
      { label: 'Population Momentum', weight: '35%' },
      { label: 'Housing Signal',      weight: '15%' },
      { label: 'Quality of Life',     weight: '10%' },
    ],
    office: [
      { label: 'Economic Velocity',   weight: '40%' },
      { label: 'Quality of Life',     weight: '30%' },
      { label: 'Population Momentum', weight: '20%' },
      { label: 'Housing Signal',      weight: '10%' },
    ],
    retail: [
      { label: 'Population Momentum', weight: '35%' },
      { label: 'Economic Velocity',   weight: '30%' },
      { label: 'Quality of Life',     weight: '25%' },
      { label: 'Housing Signal',      weight: '10%' },
    ],
    all: [],
  };

  // Scatter plot
  const W = 700, H = 460, PAD = 58;

  const getScore = (m: MetroRow, axis: string): number => {
    if (axis === 'economic')   return m.score_economic   ?? 50;
    if (axis === 'housing')    return m.score_housing    ?? 50;
    if (axis === 'population') return m.score_population ?? 50;
    if (axis === 'quality')    return m.score_quality    ?? 50;
    return 50;
  };

  const toX = (v: number) => PAD + (v / 100) * (W - PAD * 2);
  const toY = (v: number) => H - PAD - (v / 100) * (H - PAD * 2);

  const dotR = (m: MetroRow): number => {
    const pop = m.population ?? 0;
    if (pop >= 5_000_000) return 8;
    if (pop >= 1_000_000) return 6;
    if (pop >= 500_000)   return 4.5;
    if (pop >= 250_000)   return 3.5;
    return 2.5;
  };

  const dotFill = (m: MetroRow): string => {
    if (colorBy === 'trend') return trendColor(m.trend_direction);
    if (colorBy === 'size') {
      const pop = m.population ?? 0;
      if (pop >= 1_000_000) return primary;
      if (pop >= 500_000)   return secondary;
      return muted;
    }
    const s = m.score_total ?? 50;
    if (s >= 70) return green;
    if (s >= 55) return gold;
    return red;
  };

  const axisLabels: Record<string, string> = {
    economic:   'Economic Velocity',
    housing:    'Housing Signal',
    population: 'Population Momentum',
    quality:    'Quality of Life',
  };

  const creRanked: MetroWithCRE[] = [...filtered]
    .map(m => ({ ...m, creScore: creScore(m, creType) }))
    .sort((a, b) => b.creScore - a.creScore)
    .slice(0, 50);

  const hovered = hoveredCbsa ? filtered.find(m => m.cbsa_code === hoveredCbsa) ?? null : null;

  const selSt: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: '#f2ede6',
    border: `1px solid ${border}`,
    color: primary,
    fontFamily: fMono,
    fontSize: '11px',
    cursor: 'pointer',
    outline: 'none',
  };

  const creLabels: Record<CreType, string> = {
    all:         'All Asset Classes',
    multifamily: 'Multifamily',
    industrial:  'Industrial / Logistics',
    office:      'Office',
    retail:      'Retail',
  };

  if (loading) return (
    <div style={{ padding: '48px 0', color: muted, fontFamily: fBody, fontSize: 15, fontStyle: 'italic' }}>
      Loading market intelligence data…
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 6 }}>
          Market Intelligence Engine
        </div>
        <div style={{ fontSize: '28px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.1, marginBottom: 6 }}>
          The Brain
        </div>
        <div style={{ fontSize: '14px', fontFamily: fBody, color: muted, fontStyle: 'italic' }}>
          {filtered.length.toLocaleString()} metropolitan areas mapped across investment dimensions ·
          Commercial real estate opportunity scoring by asset class
        </div>
      </div>

      {/* ── Scatter controls ── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {([
          ['X Axis', xAxis, (v: string) => setXAxis(v as typeof xAxis)],
          ['Y Axis', yAxis, (v: string) => setYAxis(v as typeof yAxis)],
        ] as const).map(([lbl, val, setter], i) => (
          <div key={i}>
            <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>{lbl}</div>
            <select value={val} onChange={e => setter(e.target.value)} style={selSt}>
              <option value="economic">Economic Velocity</option>
              <option value="housing">Housing Signal</option>
              <option value="population">Population Momentum</option>
              <option value="quality">Quality of Life</option>
            </select>
          </div>
        ))}
        <div>
          <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Color By</div>
          <select value={colorBy} onChange={e => setColorBy(e.target.value as typeof colorBy)} style={selSt}>
            <option value="total">Overall Score</option>
            <option value="trend">Market Trend</option>
            <option value="size">Market Size</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: '9px', color: muted, fontFamily: fMono, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>Min Population</div>
          <select value={sizeFilter} onChange={e => setSizeFilter(e.target.value)} style={selSt}>
            <option value="">All Markets</option>
            <option value="1000000">Major (1M+)</option>
            <option value="500000">Large (500K+)</option>
            <option value="250000">Mid-size (250K+)</option>
            <option value="100000">Small (100K+)</option>
          </select>
        </div>
      </div>

      {/* ── Scatter Plot ── */}
      <div style={{ backgroundColor: white, border: `1px solid ${border}`, padding: '20px 20px 12px', marginBottom: 28, position: 'relative' }}>
        {/* Hover tooltip */}
        {hovered && (
          <div style={{
            position: 'absolute', top: 20, right: 20, backgroundColor: surface,
            border: `1px solid ${border}`, padding: '14px 18px', zIndex: 10, minWidth: 220,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontSize: '14px', fontFamily: fHead, fontWeight: 700, color: primary, marginBottom: 3, lineHeight: 1.2 }}>
              {hovered.name}
            </div>
            <div style={{ fontSize: '10px', fontFamily: fMono, color: muted, marginBottom: 10 }}>
              Rank #{hovered.rank} · {fmtPop(hovered.population)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 14px', marginBottom: 8 }}>
              {([
                ['Overall',    hovered.score_total],
                ['Economic',   hovered.score_economic],
                ['Housing',    hovered.score_housing],
                ['Population', hovered.score_population],
                ['Quality',    hovered.score_quality],
              ] as [string, number | null][]).map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: '8px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1px' }}>{l}</div>
                  <div style={{ fontSize: '13px', fontFamily: fMono, fontWeight: 700, color: scoreColor(v) }}>{fmt(v)}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', fontFamily: fMono, fontWeight: 700, color: trendColor(hovered.trend_direction) }}>
              {trendLabel(hovered.trend_direction)}
            </div>
          </div>
        )}

        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
          {/* Axis bands */}
          <rect x={toX(50)} y={PAD} width={toX(100) - toX(50)} height={toY(50) - PAD}
            fill={gold} opacity={0.04} />

          {/* Grid + tick labels */}
          {[0, 25, 50, 75, 100].map(v => (
            <g key={v}>
              <line x1={toX(v)} y1={PAD - 4} x2={toX(v)} y2={H - PAD}
                stroke={v === 50 ? border : '#e0d8cc'} strokeWidth={v === 50 ? 1.5 : 1}
                strokeDasharray={v === 50 ? '6,4' : '2,5'} />
              <line x1={PAD} y1={toY(v)} x2={W - PAD + 4} y2={toY(v)}
                stroke={v === 50 ? border : '#e0d8cc'} strokeWidth={v === 50 ? 1.5 : 1}
                strokeDasharray={v === 50 ? '6,4' : '2,5'} />
              <text x={toX(v)} y={H - PAD + 16} textAnchor="middle" fontSize={9}
                fill={muted} fontFamily={fMono}>{v}</text>
              <text x={PAD - 8} y={toY(v) + 3.5} textAnchor="end" fontSize={9}
                fill={muted} fontFamily={fMono}>{v}</text>
            </g>
          ))}

          {/* Quadrant labels */}
          <text x={toX(75)} y={toY(77)} textAnchor="middle" fontSize={8.5}
            fill={gold} fontFamily={fMono} letterSpacing={1.8} opacity={0.85}>
            HIGH GROWTH · STRONG SIGNAL
          </text>
          <text x={toX(25)} y={toY(77)} textAnchor="middle" fontSize={8.5}
            fill={muted} fontFamily={fMono} letterSpacing={1.8} opacity={0.7}>
            LOW GROWTH · STRONG SIGNAL
          </text>
          <text x={toX(75)} y={toY(23) + 14} textAnchor="middle" fontSize={8.5}
            fill={muted} fontFamily={fMono} letterSpacing={1.8} opacity={0.7}>
            HIGH GROWTH · WEAK SIGNAL
          </text>
          <text x={toX(25)} y={toY(23) + 14} textAnchor="middle" fontSize={8.5}
            fill={red} fontFamily={fMono} letterSpacing={1.8} opacity={0.55}>
            LOW GROWTH · WEAK SIGNAL
          </text>

          {/* Non-hovered dots */}
          {filtered
            .filter(m => m.cbsa_code !== hoveredCbsa)
            .map(m => (
              <circle
                key={m.cbsa_code}
                cx={toX(getScore(m, xAxis))}
                cy={toY(getScore(m, yAxis))}
                r={dotR(m)}
                fill={dotFill(m)}
                opacity={0.65}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredCbsa(m.cbsa_code)}
                onMouseLeave={() => setHoveredCbsa(null)}
                onClick={() => onOpenDetail(m.cbsa_code)}
              />
            ))}

          {/* Hovered dot — rendered on top */}
          {hovered && (() => {
            const cx = toX(getScore(hovered, xAxis));
            const cy = toY(getScore(hovered, yAxis));
            const r  = dotR(hovered);
            return (
              <g>
                <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke={gold} strokeWidth={1.5} opacity={0.5} />
                <circle
                  cx={cx} cy={cy} r={r + 1}
                  fill={dotFill(hovered)} opacity={1}
                  style={{ cursor: 'pointer' }}
                  onMouseLeave={() => setHoveredCbsa(null)}
                  onClick={() => onOpenDetail(hovered.cbsa_code)}
                />
                <text x={cx} y={cy - r - 7} textAnchor="middle" fontSize={10}
                  fontWeight={700} fill={primary} fontFamily={fMono}
                  style={{ pointerEvents: 'none' }}>
                  {hovered.name.split(',')[0]}
                </text>
              </g>
            );
          })()}

          {/* Axis labels */}
          <text x={W / 2} y={H - 6} textAnchor="middle" fontSize={10}
            fill={primary} fontFamily={fMono} fontWeight={700} letterSpacing={2}>
            {axisLabels[xAxis].toUpperCase()}
          </text>
          <text
            x={14} y={H / 2} textAnchor="middle" fontSize={10}
            fill={primary} fontFamily={fMono} fontWeight={700} letterSpacing={2}
            transform={`rotate(-90, 14, ${H / 2})`}>
            {axisLabels[yAxis].toUpperCase()}
          </text>

          {/* Legend */}
          {colorBy === 'total' && (
            <g transform={`translate(${W - PAD - 90}, ${PAD + 8})`}>
              {[
                { c: green, l: 'Score ≥ 70' },
                { c: gold,  l: 'Score 55–70' },
                { c: red,   l: 'Score < 55' },
              ].map(({ c, l }, i) => (
                <g key={l} transform={`translate(0,${i * 17})`}>
                  <circle cx={5} cy={5} r={4} fill={c} opacity={0.7} />
                  <text x={14} y={9} fontSize={9} fill={muted} fontFamily={fMono}>{l}</text>
                </g>
              ))}
              <g transform="translate(0,57)">
                <circle cx={5} cy={5} r={3} fill={muted} opacity={0.5} />
                <text x={14} y={9} fontSize={9} fill={muted} fontFamily={fMono}>Small market</text>
              </g>
              <g transform="translate(0,74)">
                <circle cx={5} cy={5} r={6} fill={muted} opacity={0.5} />
                <text x={14} y={9} fontSize={9} fill={muted} fontFamily={fMono}>Large market</text>
              </g>
            </g>
          )}
          {colorBy === 'trend' && (
            <g transform={`translate(${W - PAD - 90}, ${PAD + 8})`}>
              {[
                { c: green,  l: '↑ Rising' },
                { c: gold,   l: '→ Stable' },
                { c: red,    l: '↓ Declining' },
              ].map(({ c, l }, i) => (
                <g key={l} transform={`translate(0,${i * 17})`}>
                  <circle cx={5} cy={5} r={4} fill={c} opacity={0.7} />
                  <text x={14} y={9} fontSize={9} fill={muted} fontFamily={fMono}>{l}</text>
                </g>
              ))}
            </g>
          )}
          {colorBy === 'size' && (
            <g transform={`translate(${W - PAD - 90}, ${PAD + 8})`}>
              {[
                { c: primary,   l: 'Major (1M+)' },
                { c: secondary, l: 'Large (500K+)' },
                { c: muted,     l: 'Smaller' },
              ].map(({ c, l }, i) => (
                <g key={l} transform={`translate(0,${i * 17})`}>
                  <circle cx={5} cy={5} r={4} fill={c} opacity={0.7} />
                  <text x={14} y={9} fontSize={9} fill={muted} fontFamily={fMono}>{l}</text>
                </g>
              ))}
            </g>
          )}
        </svg>

        <div style={{ fontSize: '10px', fontFamily: fMono, color: muted, marginTop: 6, letterSpacing: '0.5px' }}>
          Dot size = population magnitude · Click any market to open full detail · {filtered.length} markets shown
        </div>
      </div>

      {/* ── CRE Opportunity Engine ── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 6 }}>
          Commercial Real Estate Opportunity Analysis
        </div>
        <div style={{ fontSize: '14px', fontFamily: fBody, color: muted, fontStyle: 'italic', marginBottom: 20, lineHeight: 1.6 }}>
          Composite opportunity scores weighted by asset class characteristics —
          employment dynamics, population trajectory, housing market liquidity,
          and institutional quality of life drivers. Top 50 of {filtered.length} markets.
        </div>

        {/* Asset class tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${border}`, marginBottom: 0 }}>
          {(['all', 'multifamily', 'industrial', 'office', 'retail'] as CreType[]).map(type => (
            <button
              key={type}
              onClick={() => setCreType(type)}
              style={{
                padding: '8px 18px',
                background: 'none',
                border: 'none',
                borderBottom: creType === type ? `2px solid ${gold}` : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                color: creType === type ? gold : muted,
                fontFamily: fMono,
                fontSize: '10px',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                fontWeight: creType === type ? 700 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {creLabels[type]}
            </button>
          ))}
        </div>

        {/* Weight strip */}
        <div style={{ backgroundColor: surface, border: `1px solid ${border}`, borderTop: 'none', padding: '10px 20px', marginBottom: 1, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          {creType === 'all' ? (
            <span style={{ fontSize: '10px', fontFamily: fMono, color: muted, fontStyle: 'italic' }}>
              Equal composite weighting across all four asset class models
            </span>
          ) : (
            creWeights[creType].map(({ label, weight }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: '9px', fontFamily: fMono, color: muted, textTransform: 'uppercase', letterSpacing: '1px' }}>{label}</span>
                <span style={{ fontSize: '12px', fontFamily: fMono, fontWeight: 700, color: primary }}>{weight}</span>
              </div>
            ))
          )}
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '52px 1fr 88px 80px 80px 80px 80px 88px 96px',
          backgroundColor: primary,
        }}>
          {['Rank', 'Metropolitan Area', creType === 'all' ? 'Score' : `${creLabels[creType]} Score`, 'Economic', 'Housing', 'Population', 'Quality', 'Population', 'Trend'].map(h => (
            <div key={h} style={{ padding: '10px 12px', fontSize: '8.5px', fontFamily: fMono, color: 'rgba(255,255,255,0.65)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700 }}>{h}</div>
          ))}
        </div>

        {/* Table rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: border }}>
          {creRanked.map((m, i) => {
            const rowBg = i % 2 === 0 ? white : bg;
            return (
              <div
                key={m.cbsa_code}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '52px 1fr 88px 80px 80px 80px 80px 88px 96px',
                  backgroundColor: rowBg,
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = goldDim)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = rowBg)}
                onClick={() => onOpenDetail(m.cbsa_code)}
              >
                <div style={{ padding: '13px 12px', fontSize: '12px', fontFamily: fMono, color: border, fontWeight: 700 }}>
                  {i + 1}
                </div>
                <div style={{ padding: '13px 12px' }}>
                  <div style={{ fontSize: '13px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.2 }}>{m.name}</div>
                  <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, marginTop: 2 }}>{m.metro_type}</div>
                </div>
                <div style={{ padding: '13px 12px' }}>
                  <div style={{ fontSize: '17px', fontFamily: fMono, fontWeight: 900, color: scoreColor(m.creScore), lineHeight: 1 }}>{m.creScore.toFixed(1)}</div>
                </div>
                <div style={{ padding: '13px 12px', fontSize: '13px', fontFamily: fMono, color: scoreColor(m.score_economic), fontWeight: 600 }}>{fmt(m.score_economic)}</div>
                <div style={{ padding: '13px 12px', fontSize: '13px', fontFamily: fMono, color: scoreColor(m.score_housing), fontWeight: 600 }}>{fmt(m.score_housing)}</div>
                <div style={{ padding: '13px 12px', fontSize: '13px', fontFamily: fMono, color: scoreColor(m.score_population), fontWeight: 600 }}>{fmt(m.score_population)}</div>
                <div style={{ padding: '13px 12px', fontSize: '13px', fontFamily: fMono, color: scoreColor(m.score_quality), fontWeight: 600 }}>{fmt(m.score_quality)}</div>
                <div style={{ padding: '13px 12px', fontSize: '12px', fontFamily: fMono, color: muted }}>{fmtPop(m.population)}</div>
                <div style={{ padding: '13px 12px', fontSize: '11px', fontFamily: fMono, fontWeight: 700, color: trendColor(m.trend_direction) }}>{trendLabel(m.trend_direction)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Searcher({ token }: Props) {
  const headers = { Authorization: `Bearer ${token}` };

  // Rankings state
  const [metros, setMetros] = useState<MetroRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  // Filters
  const [search, setSearch] = useState('');
  const [trendFilter, setTrendFilter] = useState('');
  const [categorySort, setCategorySort] = useState('');
  const [minPop, setMinPop] = useState('');

  // Subtab
  const [subtab, setSubtab] = useState<'rankings' | 'map' | 'brain' | 'market-data'>('rankings');

  // Detail panel
  const [detail, setDetail] = useState<MetroDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [thesisLoading, setThesisLoading] = useState(false);

  // ── Load rankings ──────────────────────────────────────────────────────────
  const loadRankings = useCallback(async (newOffset = 0, append = false) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit: LIMIT, offset: newOffset };
      if (trendFilter) params.trend = trendFilter;
      if (categorySort) params.category = categorySort;
      if (minPop) params.min_population = parseInt(minPop);

      const res = await axios.get(`${API}/searcher/rankings`, { headers, params });
      const rows: MetroRow[] = res.data.metros || [];
      setTotal(res.data.total || 0);
      setMetros(prev => append ? [...prev, ...rows] : rows);
      setOffset(newOffset);
    } catch (e) {
      console.error('Searcher rankings failed:', e);
    } finally {
      setLoading(false);
    }
  }, [trendFilter, categorySort, minPop, token]);

  useEffect(() => { loadRankings(0, false); }, [trendFilter, categorySort, minPop]);

  // Client-side search filter on name
  const filtered = search.trim()
    ? metros.filter(m => m.name.toLowerCase().includes(search.toLowerCase()))
    : metros;

  // ── Load metro detail ──────────────────────────────────────────────────────
  const openDetail = async (cbsa: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/searcher/metro/${cbsa}`, { headers });
      setDetail(res.data);
    } catch (e) {
      console.error('Metro detail failed:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Generate AI thesis ─────────────────────────────────────────────────────
  const generateThesis = async (cbsa: string) => {
    setThesisLoading(true);
    try {
      const res = await axios.get(`${API}/searcher/thesis/${cbsa}`, { headers, params: { force_refresh: true } });
      setDetail(prev => prev ? { ...prev, ai_thesis: res.data.thesis } : prev);
    } catch (e) {
      console.error('Thesis generation failed:', e);
    } finally {
      setThesisLoading(false);
    }
  };

  // ── Indicator sort order for detail panel ─────────────────────────────────
  const IND_ORDER = [
    // Labor / Economy
    'employment_level', 'unemployment_rate_bls_recent', 'unemployment_rate_bls',
    'median_hhi', 'ui_claims_4wk_avg',
    // Population
    'population', 'migration_net', 'migration_inflow', 'migration_outflow',
    // Housing — price
    'redfin_sale_price_current', 'redfin_median_sale_price',
    'listing_price_current', 'listing_price',
    'house_price_index', 'median_home_value',
    // Housing — supply/demand
    'redfin_dom_current', 'redfin_median_dom',
    'listing_inventory_current', 'listing_inventory',
    'redfin_new_listings', 'building_permits_units',
    'housing_vacancy_rate', 'median_gross_rent',
    // Quality of life
    'bachelors_attainment_rate', 'median_age',
  ];

  const sortedIndicators = (inds: Record<string, Indicator>) => {
    const keys = Object.keys(inds);
    return [
      ...IND_ORDER.filter(k => keys.includes(k)),
      ...keys.filter(k => !IND_ORDER.includes(k)),
    ];
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const sl: React.CSSProperties = { fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 8 };
  const pt: React.CSSProperties = { fontSize: '28px', fontFamily: fHead, fontWeight: 700, color: primary, marginBottom: 6, lineHeight: 1.1 };
  const ps: React.CSSProperties = { fontSize: '15px', fontFamily: fBody, color: secondary, marginBottom: 24, fontStyle: 'italic' };

  // ── DETAIL PANEL ───────────────────────────────────────────────────────────
  if (detailLoading) {
    return (
      <div style={{ padding: 32, backgroundColor: bg, minHeight: '100vh' }}>
        <div style={{ color: muted, fontFamily: fBody, fontSize: 16 }}>Loading market data…</div>
      </div>
    );
  }

  if (detail) {
    const s = detail.scores;
    const indKeys = sortedIndicators(detail.indicators);

    return (
      <div style={{ backgroundColor: bg, minHeight: '100vh', padding: '24px 28px' }}>
        {/* Back */}
        <button
          onClick={() => setDetail(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: gold, fontFamily: fMono, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', padding: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          ← Back to Rankings
        </button>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={sl}>Market Detail</div>
          <div style={{ ...pt, fontSize: 24 }}>{detail.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <span style={{ fontSize: '11px', fontFamily: fMono, color: muted }}>{detail.metro_type}</span>
            {s.rank_overall && <span style={{ fontSize: '11px', fontFamily: fMono, color: muted }}>Rank #{s.rank_overall}</span>}
            <span style={{ fontSize: '11px', fontFamily: fMono, color: muted }}>{fmtPop(detail.population)} population</span>
            <span style={{ fontSize: '11px', fontFamily: fMono, color: trendColor(detail.trend_direction), fontWeight: 700 }}>
              {trendLabel(detail.trend_direction)}
            </span>
          </div>
        </div>

        {/* Score strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, backgroundColor: border, marginBottom: 24 }}>
          {[
            { label: 'Overall', score: s.total },
            { label: 'Population', score: s.population },
            { label: 'Economic', score: s.economic },
            { label: 'Housing', score: s.housing },
            { label: 'Quality', score: s.quality },
          ].map(({ label, score }) => (
            <div key={label} style={{ backgroundColor: surface, padding: '16px 20px' }}>
              <div style={{ fontSize: '9px', color: muted, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700, marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: '28px', fontWeight: 900, color: scoreColor(score), fontFamily: fMono, lineHeight: 1 }}>{fmt(score)}</div>
            </div>
          ))}
        </div>

        {/* AI Thesis */}
        <div style={{ backgroundColor: white, border: `1px solid ${border}`, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: '9px', color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>Investment Thesis</div>
            <button
              onClick={() => generateThesis(detail.cbsa_code)}
              disabled={thesisLoading}
              style={{ fontSize: '10px', fontFamily: fMono, letterSpacing: '1px', textTransform: 'uppercase', padding: '6px 14px', backgroundColor: thesisLoading ? surface : gold, color: white, border: 'none', cursor: thesisLoading ? 'default' : 'pointer', opacity: thesisLoading ? 0.7 : 1 }}
            >
              {thesisLoading ? 'Generating…' : detail.ai_thesis ? 'Regenerate' : 'Generate Thesis'}
            </button>
          </div>
          {detail.ai_thesis ? (
            <div style={{ fontSize: '14px', fontFamily: fBody, color: primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {detail.ai_thesis}
            </div>
          ) : (
            <div style={{ fontSize: '13px', fontFamily: fBody, color: muted, fontStyle: 'italic' }}>
              No thesis generated yet. Click "Generate Thesis" to create an AI-powered investment analysis for this market.
            </div>
          )}
        </div>

        {/* Indicators */}
        <div style={{ backgroundColor: white, border: `1px solid ${border}` }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, fontSize: '9px', color: gold, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: fMono, fontWeight: 700 }}>
            Key Indicators
          </div>
          {indKeys.map((key, i) => {
            const ind = detail.indicators[key];
            if (!ind || !ind.series.length) return null;
            const sorted = [...ind.series].sort((a, b) => b.year - a.year);
            const latest = sorted[0];
            const prev = sorted[1];
            const change = prev ? ((latest.value - prev.value) / Math.abs(prev.value)) * 100 : null;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '12px 20px', backgroundColor: i % 2 === 0 ? white : bg, borderBottom: `1px solid ${border}` }}>
                <div style={{ flex: '0 0 220px' }}>
                  <div style={{ fontSize: '12px', fontFamily: fBody, color: primary, fontWeight: 600 }}>{IND_LABELS[key] || key}</div>
                  <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{ind.source}</div>
                </div>
                <div style={{ flex: '0 0 140px' }}>
                  <div style={{ fontSize: '15px', fontFamily: fMono, fontWeight: 700, color: primary }}>{fmtVal(latest.value, ind.unit)}</div>
                  <div style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{latest.year}</div>
                </div>
                {change != null && (
                  <div style={{ flex: '0 0 80px', fontSize: '12px', fontFamily: fMono, color: change >= 0 ? green : red, fontWeight: 600 }}>
                    {change >= 0 ? '+' : ''}{change.toFixed(1)}% YoY
                  </div>
                )}
                <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {sorted.slice(0, 6).map(pt => (
                    <span key={pt.year} style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>
                      {pt.year}: {fmtVal(pt.value, ind.unit)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── RANKINGS LIST ──────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: bg, minHeight: '100vh', padding: '24px 28px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={sl}>Real Estate Market Intelligence</div>
        <div style={pt}>Market Searcher</div>
        <div style={ps}>
          {total > 0 ? `${total.toLocaleString()} markets ranked by investment momentum` : 'Loading market data…'}
        </div>
      </div>

      {/* Subtab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: `2px solid ${border}` }}>
        {([['rankings', 'Market Rankings'], ['map', 'US Market Map'], ['brain', 'The Brain'], ['market-data', 'Live Market Data']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSubtab(id)}
            style={{
              padding: '9px 20px',
              background: 'none',
              border: 'none',
              borderBottom: subtab === id ? `2px solid ${gold}` : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              color: subtab === id ? gold : muted,
              fontFamily: fMono,
              fontSize: '11px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              fontWeight: subtab === id ? 700 : 400,
              transition: 'color 0.15s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Market Data subtab */}
      {subtab === 'market-data' && <MarketData token={token} />}

      {/* US Map subtab */}
      {subtab === 'map' && <USMap token={token} onOpenDetail={openDetail} />}

      {/* The Brain subtab */}
      {subtab === 'brain' && <BrainPanel token={token} />}

      {/* Filters — only shown on rankings subtab */}
      {subtab === 'rankings' && <>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <input
          type="text"
          placeholder="Search markets…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', minWidth: 180, padding: '9px 14px', backgroundColor: '#f2ede6', border: `1px solid ${border}`, color: primary, fontFamily: fBody, fontSize: '14px', outline: 'none' }}
        />

        {/* Trend */}
        <select
          value={trendFilter}
          onChange={e => setTrendFilter(e.target.value)}
          style={{ padding: '9px 14px', backgroundColor: '#f2ede6', border: `1px solid ${border}`, color: primary, fontFamily: fMono, fontSize: '11px', cursor: 'pointer', outline: 'none' }}
        >
          <option value="">All Trends</option>
          <option value="rising">↑ Rising</option>
          <option value="stable">→ Stable</option>
          <option value="declining">↓ Declining</option>
        </select>

        {/* Category sort */}
        <select
          value={categorySort}
          onChange={e => setCategorySort(e.target.value)}
          style={{ padding: '9px 14px', backgroundColor: '#f2ede6', border: `1px solid ${border}`, color: primary, fontFamily: fMono, fontSize: '11px', cursor: 'pointer', outline: 'none' }}
        >
          <option value="">Sort: Overall</option>
          <option value="population">Sort: Population Momentum</option>
          <option value="economic">Sort: Economic Velocity</option>
          <option value="housing">Sort: Housing Signal</option>
          <option value="quality">Sort: Quality of Life</option>
        </select>

        {/* Min population */}
        <select
          value={minPop}
          onChange={e => setMinPop(e.target.value)}
          style={{ padding: '9px 14px', backgroundColor: '#f2ede6', border: `1px solid ${border}`, color: primary, fontFamily: fMono, fontSize: '11px', cursor: 'pointer', outline: 'none' }}
        >
          <option value="">All Sizes</option>
          <option value="1000000">Major (1M+)</option>
          <option value="500000">Large (500K+)</option>
          <option value="250000">Mid-size (250K+)</option>
          <option value="100000">Small (100K+)</option>
        </select>
      </div>

      {/* Metro cards */}
      {loading && metros.length === 0 ? (
        <div style={{ color: muted, fontFamily: fBody, fontSize: 16, padding: '40px 0' }}>Loading rankings…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: border }}>
          {filtered.map((m, idx) => (
            <div
              key={m.cbsa_code}
              onClick={() => openDetail(m.cbsa_code)}
              style={{ backgroundColor: white, padding: '16px 20px', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = goldDim)}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = white)}
            >
              {/* Top row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ fontSize: '18px', fontFamily: fMono, fontWeight: 700, color: border, width: 36, flexShrink: 0 }}>
                    #{m.rank}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontFamily: fHead, fontWeight: 700, color: primary, lineHeight: 1.2 }}>{m.name}</div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
                      <span style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{m.metro_type}</span>
                      {m.population && <span style={{ fontSize: '10px', fontFamily: fMono, color: muted }}>{fmtPop(m.population)}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '26px', fontFamily: fMono, fontWeight: 900, color: scoreColor(m.score_total), lineHeight: 1 }}>
                      {fmt(m.score_total)}
                    </div>
                    <div style={{ fontSize: '9px', fontFamily: fMono, color: muted, letterSpacing: '1px', textTransform: 'uppercase' }}>Overall</div>
                  </div>
                  <div style={{ fontSize: '11px', fontFamily: fMono, fontWeight: 700, color: trendColor(m.trend_direction), textAlign: 'right', minWidth: 72 }}>
                    {trendLabel(m.trend_direction)}
                  </div>
                </div>
              </div>

              {/* Score bars */}
              <div style={{ display: 'flex', gap: 16 }}>
                <ScoreBar score={m.score_population} label="Population" />
                <ScoreBar score={m.score_economic}   label="Economic" />
                <ScoreBar score={m.score_housing}    label="Housing" />
                <ScoreBar score={m.score_quality}    label="Quality" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {!search && filtered.length < total && (
        <div style={{ textAlign: 'center', paddingTop: 20 }}>
          <button
            onClick={() => loadRankings(offset + LIMIT, true)}
            disabled={loading}
            style={{ padding: '10px 28px', backgroundColor: 'transparent', border: `1px solid ${border}`, color: primary, fontFamily: fMono, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Loading…' : `Load More (${total - filtered.length - offset} remaining)`}
          </button>
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div style={{ color: muted, fontFamily: fBody, fontSize: 15, padding: '40px 0', fontStyle: 'italic' }}>
          No markets match your filters.
        </div>
      )}
      </>}
    </div>
  );
}

